/*
 * The one HTTP path for the console. Everything that used to be a $.ajax call
 * -- the four Ajax* helpers, the top-menu navigation, tab loading -- goes
 * through request(), which owns the four cross-cutting behaviours the jQuery
 * prefilters used to add: base-path resolution, the antiforgery header on
 * unsafe methods, the X-Requested-With pin the AppShell filter branches on,
 * and the step-up redirect on a 401 carrying RequireRecentAuth's envelope.
 */

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';

/** A serialized form control, the shape jQuery's serializeArray() produced. */
export interface NameValue {
    name: string;
    value: string;
}

/** Request bodies the legacy call sites pass: serializeArray() pairs, plain objects, strings, FormData. */
export type RequestData = NameValue[] | Record<string, unknown> | string | FormData | URLSearchParams | null | undefined;

export interface RequestOptions {
    method?: Method | string;
    data?: RequestData;
    /**
     * Mirrors jQuery's option: undefined = form-encoded; 'application/json' =
     * JSON body (objects are stringified); false = let the body set its own
     * (FormData uploads).
     */
    contentType?: string | false;
    headers?: Record<string, string>;
    /** Extra Accept header hint; the server chooses the format from the request anyway. */
    accept?: 'html' | 'json' | 'any';
    signal?: AbortSignal;
}

/**
 * What the legacy error callbacks read off jQuery's jqXHR. Built from the
 * fetch Response so un-migrated handlers keep working unchanged.
 */
export interface XhrLike {
    status: number;
    statusText: string;
    responseText: string;
    responseJSON?: unknown;
    getResponseHeader(name: string): string | null;
}

export interface HttpResult {
    response: Response;
    xhr: XhrLike;
    text: string;
    /** Parsed when the response is JSON; undefined otherwise. */
    json?: unknown;
}

/** Thrown for a non-2xx response or a network failure; carries the jqXHR-shaped view. */
export class HttpError extends Error {
    constructor(
        public readonly xhr: XhrLike,
        /** jQuery's textStatus: 'error' for HTTP failures, 'abort', 'timeout', 'parsererror'. */
        public readonly textStatus: string,
        message?: string,
    ) {
        super(message ?? (xhr.statusText || textStatus));
        this.name = 'HttpError';
    }
}

// ---------------------------------------------------------------- URLs

/** Base path the layout published (window.mcpAjax.baseUrl); '/' when unset. */
export function baseUrl(): string {
    return window.mcpAjax?.baseUrl || '/';
}

/**
 * Resolve an app-relative path ('Person/List') against the deployment's base
 * path. Rooted ('/Home/Foo') and absolute ('https://…') URLs pass through, so
 * a caller can hand over either form -- naively prepending '/' to '/Home/Foo'
 * would yield '//Home/Foo', which the browser reads as protocol-relative.
 */
export function url(path: string | null | undefined): string {
    if (!path) return baseUrl();
    if (path.charAt(0) === '/' || /^[a-z][a-z0-9+.-]*:/i.test(path)) return path;
    return baseUrl() + path;
}

// ---------------------------------------------------------------- antiforgery + step-up

/** Read at call time: the token rotates across sign-in/out and any re-rendered layout carries a fresh one. */
export function antiForgeryToken(): string {
    const input = document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]');
    return input?.value ?? '';
}

/**
 * RequireRecentAuth answers a sensitive request from a stale session with 401
 * + { stepUpRequired: true, returnUrl }. An XHR cannot follow the OIDC
 * redirect a page load would get, so the top-level frame navigates to the
 * step-up endpoint instead. Returns true when the caller should abandon its
 * own error handling.
 */
export function handleStepUp(xhr: XhrLike | null | undefined): boolean {
    if (!xhr || xhr.status !== 401) return false;
    let envelope: unknown = xhr.responseJSON;
    if (envelope === undefined) {
        const contentType = xhr.getResponseHeader('Content-Type') ?? '';
        if (contentType.includes('application/json') && xhr.responseText) {
            try { envelope = JSON.parse(xhr.responseText); } catch { /* not JSON */ }
        }
    }
    if (!isRecord(envelope) || envelope['stepUpRequired'] !== true) return false;

    let returnUrl = typeof envelope['returnUrl'] === 'string' && envelope['returnUrl']
        ? envelope['returnUrl']
        : window.location.href;
    if (!returnUrl.includes('authRefreshed=1')) {
        returnUrl += (returnUrl.includes('?') ? '&' : '?') + 'authRefreshed=1';
    }
    const stepUp = url(window.mcpAjax?.stepUpUrl || '/Home/StepUp');
    window.location.href = `${stepUp}?returnUrl=${encodeURIComponent(returnUrl)}`;
    return true;
}

// ---------------------------------------------------------------- busy indicator

let inFlight = 0;
let busyElement: HTMLElement | null = null;

/**
 * Global "Working..." pill, top-right, visible while any request is in flight.
 * A backstop so a handler that forgets its per-element spinner still gives
 * visible feedback; per-element `busy` options work alongside it.
 */
function setBusy(delta: number): void {
    inFlight = Math.max(0, inFlight + delta);
    if (!busyElement) {
        if (!document.body) return;
        busyElement = document.createElement('div');
        busyElement.id = 'McpAjaxBusy';
        busyElement.className = 'position-fixed top-0 end-0 m-3 px-3 py-2 bg-light border rounded shadow-sm d-none';
        busyElement.style.zIndex = '1080';
        busyElement.innerHTML = '<i class="fas fa-spinner fa-spin text-primary me-2"></i><span class="text-secondary">Working...</span>';
        document.body.appendChild(busyElement);
    }
    busyElement.classList.toggle('d-none', inFlight === 0);
}

/** Number of requests currently in flight (tests, diagnostics). */
export function pending(): number {
    return inFlight;
}

// ---------------------------------------------------------------- encoding

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNameValueArray(value: unknown): value is NameValue[] {
    return Array.isArray(value) && value.every(v => isRecord(v) && typeof v['name'] === 'string');
}

/**
 * jQuery.param semantics, because the Web's binders were written against
 * them: arrays become `key[]=v`, nested objects `key[sub]=v`, null/undefined
 * an empty value, booleans/numbers their string form.
 */
export function encodeParams(data: NameValue[] | Record<string, unknown> | URLSearchParams): URLSearchParams {
    if (data instanceof URLSearchParams) return data;
    const params = new URLSearchParams();
    const add = (key: string, value: unknown): void => {
        if (value === null || value === undefined) {
            params.append(key, '');
        } else if (Array.isArray(value)) {
            for (const item of value) {
                add(isRecord(item) ? `${key}[]` : `${key}[]`, item);
            }
        } else if (value instanceof Date) {
            params.append(key, value.toISOString());
        } else if (isRecord(value)) {
            for (const [sub, subValue] of Object.entries(value)) {
                add(`${key}[${sub}]`, subValue);
            }
        } else {
            params.append(key, String(value));
        }
    };
    if (isNameValueArray(data)) {
        for (const pair of data) params.append(pair.name, pair.value ?? '');
    } else {
        for (const [key, value] of Object.entries(data)) add(key, value);
    }
    return params;
}

interface EncodedBody {
    body: BodyInit | null;
    contentType: string | null;
}

function encodeBody(data: RequestData, contentType: string | false | undefined): EncodedBody {
    if (data === null || data === undefined) {
        return { body: null, contentType: null };
    }
    if (data instanceof FormData) {
        return { body: data, contentType: null };          // the browser sets the multipart boundary
    }
    if (typeof data === 'string') {
        return { body: data, contentType: contentType || 'application/x-www-form-urlencoded; charset=UTF-8' };
    }
    if (contentType && contentType.includes('json')) {
        return { body: JSON.stringify(data), contentType };
    }
    return { body: encodeParams(data), contentType: contentType || 'application/x-www-form-urlencoded; charset=UTF-8' };
}

function isUnsafe(method: string): boolean {
    return method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
}

// ---------------------------------------------------------------- request

function xhrOf(response: Response | null, text: string, json: unknown): XhrLike {
    return {
        status: response?.status ?? 0,
        statusText: response?.statusText ?? '',
        responseText: text,
        responseJSON: json,
        getResponseHeader: name => response?.headers.get(name) ?? null,
    };
}

/**
 * Perform a request. Resolves for any 2xx; throws HttpError for everything
 * else (after the step-up check, which navigates away and still throws so the
 * caller stops). GET data becomes the query string; other methods get an
 * encoded body per the rules in encodeBody().
 */
export async function request(path: string, options: RequestOptions = {}): Promise<HttpResult> {
    const method = (options.method ?? 'GET').toUpperCase();
    let target = url(path);
    const headers: Record<string, string> = {
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.headers ?? {}),
    };
    if (options.accept === 'json') headers['Accept'] = 'application/json, text/javascript, */*; q=0.01';
    else if (options.accept === 'html') headers['Accept'] = 'text/html, */*; q=0.01';

    let body: BodyInit | null = null;
    if (method === 'GET' || method === 'HEAD') {
        if (options.data !== null && options.data !== undefined && !(options.data instanceof FormData)) {
            const query = typeof options.data === 'string'
                ? options.data
                : encodeParams(options.data).toString();
            if (query) target += (target.includes('?') ? '&' : '?') + query;
        }
    } else {
        const encoded = encodeBody(options.data, options.contentType);
        body = encoded.body;
        if (encoded.contentType) headers['Content-Type'] = encoded.contentType;
    }
    if (isUnsafe(method)) {
        const token = antiForgeryToken();
        if (token) headers['RequestVerificationToken'] = token;
    }

    setBusy(+1);
    let response: Response;
    try {
        response = await fetch(target, {
            method,
            headers,
            body,
            credentials: 'same-origin',
            signal: options.signal ?? null,
        });
    } catch (error) {
        setBusy(-1);
        const aborted = error instanceof DOMException && error.name === 'AbortError';
        throw new HttpError(xhrOf(null, '', undefined), aborted ? 'abort' : 'error', aborted ? 'abort' : 'Network error');
    }

    let text = '';
    let json: unknown;
    try {
        text = await response.text();
        const contentType = response.headers.get('Content-Type') ?? '';
        if (text && contentType.includes('application/json')) {
            try { json = JSON.parse(text); } catch { json = undefined; }
        }
    } finally {
        setBusy(-1);
    }

    const xhr = xhrOf(response, text, json);
    if (!response.ok) {
        handleStepUp(xhr);
        throw new HttpError(xhr, 'error', response.statusText);
    }
    return { response, xhr, text, json };
}

/** Convenience: the HttpError's xhr view, or a synthetic one for anything else. */
export function xhrFromError(error: unknown): XhrLike {
    if (error instanceof HttpError) return error.xhr;
    const message = error instanceof Error ? error.message : String(error);
    return xhrOf(null, message, undefined);
}

/** jQuery's textStatus for an error, for the (xhr, status, error) callback shape. */
export function statusFromError(error: unknown): string {
    return error instanceof HttpError ? error.textStatus : 'error';
}

/**
 * Flatten an ASP.NET ModelState dictionary ({ field: [msg, ...] }) into one
 * readable line. Keys beginning "__" are handler hints, never user-facing.
 */
export function modelStateSummary(modelState: unknown): string {
    if (!isRecord(modelState)) return '';
    const messages: string[] = [];
    for (const [key, value] of Object.entries(modelState)) {
        if (key.startsWith('__')) continue;
        if (Array.isArray(value)) {
            for (const item of value) if (item) messages.push(String(item));
        } else if (value) {
            messages.push(String(value));
        }
    }
    return messages.join(' · ');
}

/** Shape of the Web's JsonModelStateResult envelope. */
export interface JsonEnvelope {
    success: boolean;
    modelState?: Record<string, string[]>;
    message?: string;
    [key: string]: unknown;
}

export function isJsonEnvelope(value: unknown): value is JsonEnvelope {
    return isRecord(value) && typeof value['success'] === 'boolean';
}
